import { useEffect, useRef, useState } from 'react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'

import { jobApi } from '@/api/endpoints'
import type { AutoApplyFilters, AutoApplySearchResult } from '@/types/api'
import { cleanFilters, filtersKey } from './filters'

export const PAGE_SIZE = 25
/** How long to wait after the last filter change before searching. */
const FILTER_DELAY = 600
/** Typing already waits in the search box; only a short extra pause here. */
const TYPING_DELAY = 150

const SEARCH_KEY = ['auto-apply', 'search'] as const
const REFRESH_KEY = ['auto-apply', 'refresh'] as const

/**
 * The Auto Apply search, in two parts per filter set:
 *
 * - the list (`refresh: false`), fast, paged: what the page shows;
 * - one pool refresh (`refresh: true`) per filter set, slow: the server
 *   searches the career-page pool for these filters, adds what fits to the
 *   user's matches, and answers with page 1, which replaces the list's.
 *
 * Filter edits are debounced; until the refresh lands the list stays
 * visible and `refreshing` is true.
 */
export function useAutoApplySearch(initial: AutoApplyFilters, enabled: boolean) {
  const queryClient = useQueryClient()
  const [filters, setFiltersState] = useState<AutoApplyFilters>(() => cleanFilters(initial))
  const [committed, setCommitted] = useState<AutoApplyFilters>(() => cleanFilters(initial))
  const draftKey = filtersKey(filters)
  const key = filtersKey(committed)
  const lastQ = useRef(committed.q)

  useEffect(() => {
    if (draftKey === key) return
    const onlyTyping = (filters.q ?? '') !== (lastQ.current ?? '') &&
      filtersKey({ ...filters, q: undefined }) === filtersKey({ ...committed, q: undefined })
    const timer = window.setTimeout(() => {
      lastQ.current = filters.q
      setCommitted(filters)
    }, onlyTyping ? TYPING_DELAY : FILTER_DELAY)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey])

  /** `immediate` skips the debounce: a chip removed, a suggestion taken, Clear all. */
  const setFilters = (next: AutoApplyFilters, immediate = false) => {
    const clean = cleanFilters(next)
    setFiltersState(clean)
    if (immediate) {
      lastQ.current = clean.q
      setCommitted(clean)
    }
  }

  // The page, kept per filter set: a new set starts at page 1.
  const [paging, setPaging] = useState({ key, page: 1 })
  const page = paging.key === key ? paging.page : 1
  const setPage = (next: number) => setPaging({ key, page: next })

  const list = useQuery({
    queryKey: [...SEARCH_KEY, key, page],
    queryFn: () => jobApi.autoApplySearch({ filters: committed, page, pageSize: PAGE_SIZE }),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })

  const refresh = useQuery({
    queryKey: [...REFRESH_KEY, key],
    queryFn: async (): Promise<AutoApplySearchResult> => {
      const result = await jobApi.autoApplySearch({ filters: committed, page: 1, pageSize: PAGE_SIZE, refresh: true })
      // The refreshed first page replaces the quick one; later pages refetch when opened.
      queryClient.setQueryData([...SEARCH_KEY, key, 1], result)
      void queryClient.invalidateQueries({
        queryKey: [...SEARCH_KEY, key],
        predicate: (query) => query.queryKey[3] !== 1,
      })
      return result
    },
    enabled,
    // The pool search can outlast the request (45–55 s on dev; the server answers at 25 s and keeps
    // going). While it says so, ask again every 10 s, for up to 3 minutes; the answer that finds it
    // done carries the new first page, and the other pages refetch.
    refetchInterval: (query) => {
      const data = query.state.data
      const pending = Boolean(data?.refreshPending ?? (data && !data.refreshed && /still searching|keeps running/i.test(data.refreshError ?? '')))
      return pending && Date.now() - (query.state.dataUpdateCount ? firstAnswerAt(key) : Date.now()) < 3 * 60_000 ? 10_000 : false
    },
    // The server refreshes a filter set at most once per 10 minutes anyway.
    staleTime: 10 * 60_000,
    gcTime: 10 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  })

  return {
    /** What the controls show (changes at once). */
    filters,
    /** What the results are for (debounced). */
    committed,
    committedKey: key,
    setFilters,
    /** Filters edited, search not sent yet. */
    pending: draftKey !== key,
    page,
    setPage,
    data: list.data,
    isLoading: list.isLoading,
    isError: list.isError && !list.data,
    retry: () => void list.refetch(),
    /** Showing the previous filter set's (or page's) results while the new ones load. */
    stale: list.isPlaceholderData,
    fetching: list.isFetching,
    refreshing: refresh.isFetching || Boolean(refresh.data?.refreshPending),
    refreshError: refresh.data?.refreshPending ? null : refresh.data?.refreshError ?? (refresh.isError ? 'The career-page search did not finish.' : null),
  }
}

/** When a filter set first got a refresh answer, to bound the follow-up polling. */
const firstAnswers = new Map<string, number>()
function firstAnswerAt(key: string): number {
  if (!firstAnswers.has(key)) firstAnswers.set(key, Date.now())
  return firstAnswers.get(key) as number
}

/** After applying: every cached list is out of date (the applied jobs drop out). */
export function invalidateAutoApplySearch(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: SEARCH_KEY })
}
